import { createUseStyles } from "react-jss";
import { ChoiceListDisplayType } from "../../../models/enums/ChoiceListDisplayType";
import { backgroundless, getSpacing } from "../../../theme/Mixins";

export default createUseStyles({
    choiceList: {
        composes: "choice-list",
        display: ({ displayType }) =>
            displayType === ChoiceListDisplayType.Horizontal ||
            displayType === ChoiceListDisplayType.HorizontalColumns
                ? "flex"
                : null,
        flexWrap: "wrap",
        alignItems: "flex-start",
        justifyContent: ({ isCarrouselScaleActive, horizontalScaleChoicesWrapped }) =>
            isCarrouselScaleActive && horizontalScaleChoicesWrapped !== true
                ? "space-between"
                : undefined,
    },
    choiceListTableHorizontal: {
        composes: "choice-list--table--horizontal",
        display: "flex",
        flexWrap: "wrap",
    },
    choiceListTableVertical: {
        composes: "choice-list--table--vertical",
    },
    choiceListGrid: {
        composes: "choice-list--grid",
        display: "grid",
        gridAutoFlow: ({ displayType }) =>
            displayType === ChoiceListDisplayType.VerticalColumns ? "column" : null,
        gridGap: ({ theme }) => (backgroundless(theme) ? 0 : getSpacing(theme, theme.ChoiceSpacing, theme.QuestionSpacing?.WithinComponents) / 2),
        gridTemplateColumns: ({ numberOfColumns }) => `repeat(${numberOfColumns}, 1fr)`,
        gridTemplateRows: ({ numberOfRows }) => `repeat(${numberOfRows}, auto)`,
        overflowWrap: "anywhere",
        "&>.choice": {
            margin: 0,
            minWidth: 125,
        },
        "& .choice__container": {
            alignItems: "start",
            minWidth: 90,
        },
    },
    checkBoxAlignment: {
        "& .choice__container": {
            flexDirection: ({ theme }) =>
                theme.CheckBoxIconAlignment === "right" ? "row-reverse" : "row",
        },
    },
    radioIconAlignment: {
        "& .choice__container": {
            flexDirection: ({ theme }) =>
                theme.RadioIconAlignment === "right" ? "row-reverse" : "row",
        },
    },
    carrouselScaleWrapper: {
        composes: "carrousel-scale",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        "& .radio__icon": {
            display: "none",
        },
        "& .choice__container .spacer": {
            display: "none",
        },
    },
    carrouselScaleLayoutPending: {
        visibility: "hidden",
    },
    carrouselScaleAnchorsHorizontal: {
        composes: "carrousel-scale__anchors--horizontal",
        display: "flex",
        justifyContent: "space-between",
        width: "100%",
        marginBottom: ({ theme }) =>
            getSpacing(theme, theme.ChoiceSpacing, theme.QuestionSpacing?.WithinComponents),
    },
    carrouselScaleAnchor: {
        composes: "carrousel-scale__anchor",
        textAlign: "center",
    },
    carrouselScaleAnchorTop: {
        marginBottom: ({ theme }) =>
            getSpacing(theme, theme.ChoiceSpacing, theme.QuestionSpacing?.WithinComponents),
    },
    carrouselScaleAnchorBottom: {
        marginTop: ({ theme }) =>
            getSpacing(theme, theme.ChoiceSpacing, theme.QuestionSpacing?.WithinComponents),
    },
    carrouselScaleHorizontalCentered: {
        composes: "carrousel-scale__horizontal-centered",
        alignItems: "center",
        display: "flex",
        flexDirection: "column",
        width: "100%",
    },
    carrouselScaleHorizontalCenteredInner: {
        width: "fit-content",
        maxWidth: "100%",
    },
    carrouselScaleAnchorsHorizontalCentered: {
        composes: "carrousel-scale__anchors--horizontal",
        display: "flex",
        justifyContent: "space-between",
        width: "100%",
        marginBottom: ({ theme }) =>
            getSpacing(theme, theme.ChoiceSpacing, theme.QuestionSpacing?.WithinComponents),
    },
    carrouselScaleNpsChoicesCentered: {
        justifyContent: "center",
        width: "100%",
        maxWidth: "100%",
    },
    carrouselScaleNpsChoices: {
        composes: "carrousel-scale__choices",
        alignItems: "stretch",
        display: "flex",
        flexWrap: "nowrap",
        margin: ({ theme }) => [[getSpacing(theme, 0, theme.QuestionSpacing?.WithinComponents), -3]],
        width: "100%",
        "& .carrousel-scale__item [role='radio']": {
            maxHeight: ({ cellHeight }: { cellHeight?: number }) => cellHeight ?? 80,
            "&:before": {
                display: "none !important",
                height: "0 !important",
                paddingTop: "0 !important",
            },
        },
    },
    carrouselScaleNpsChoicesStacked: {
        composes: "carrousel-scale__choices--stacked",
        alignItems: "stretch",
        flexDirection: "column",
        flexWrap: "nowrap",
        margin: 0,
        "& .carrousel-scale__item": {
            maxWidth: "none",
            width: "100%",
        },
        "& .carrousel-scale__item [role='radio']": {
            maxWidth: "none",
            width: "100%",
        },
    },
    carrouselScaleNpsChoicesMobile: {
        flexDirection: "column",
        margin: 0,
        "& .carrousel-scale__item": {
            maxWidth: "none",
            width: "100%",
        },
        "& .carrousel-scale__item [role='radio']": {
            maxHeight: ({ cellHeight }: { cellHeight?: number }) => `${cellHeight ?? 48}px`,
            maxWidth: "none",
            width: "100%",
        },
    },
    /** First focus target when invalid so global InputFocusRenderer does not land on the first scale cell. */
    validationFocusAnchor: {
        composes: "sr-only",
        position: "absolute",
        width: 1,
        height: 1,
        padding: 0,
        margin: -1,
        overflow: "hidden",
        clip: "rect(0, 0, 0, 0)",
        whiteSpace: "nowrap",
        border: 0,
    },
});
